import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkle, Robot, Spinner, Warning, Lightning, Lightbulb, Package,
  ArrowsClockwise, Trash, CheckCircle, Circle, ClockClockwise,
  VideoCamera, FilmReel, MusicNote, BookOpenText, Megaphone,
  ShoppingCart, TrendUp, TrendDown, ChartBar, CurrencyDollar,
  UsersThree, ChatCircle,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import TaskAssignModal from "@/components/TaskAssignModal";
import AnalysisChatPanel from "@/components/AnalysisChatPanel";
import AlendraDashboardOverview from "@/components/AlendraDashboardOverview";

/**
 * AlendraInsights — panel ejecutivo con tres tabs:
 *   1. Inventario  — alertas, acciones, oportunidades
 *   2. Marketing   — qué reels/videos/tiktok producir y para qué productos
 *   3. Compras     — qué comprar más, qué dejar de comprar, mejor margen/rotación
 */
export default function AlendraInsights() {
  const [tab, setTab] = useState("inventory");
  const TABS = [
    { id: "inventory", label: "Inventario", icon: Robot },
    { id: "marketing", label: "Marketing", icon: Megaphone },
    { id: "purchasing", label: "Compras", icon: ShoppingCart },
  ];

  return (
    <div className="space-y-4" data-testid="alendra-insights">
      <div>
        <h1 className="font-heading text-3xl font-bold">Alendra Insights</h1>
        <p className="text-sm text-zinc-500">Análisis ejecutivo del negocio con Alendra AI. Cada análisis se guarda con historial persistente.</p>
      </div>

      <AlendraDashboardOverview />

      <div className="flex border-b border-zinc-200 gap-1">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              data-testid={`insights-tab-${t.id}`}
              className={`px-4 py-2 text-sm font-semibold transition border-b-2 -mb-px inline-flex items-center gap-2 ${active ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-zinc-500 hover:text-zinc-800"}`}
            >
              <Icon size={16} weight={active ? "fill" : "regular"}/>
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === "inventory" && <InventoryInsightsTab/>}
      {tab === "marketing" && <MarketingInsightsTab/>}
      {tab === "purchasing" && <PurchasingInsightsTab/>}
    </div>
  );
}

/**
 * Hook genérico: sidebar de historial + run/delete.
 * `endpoint` debe ser una ruta sin prefijo /api (ej. "/alendra/insights/inventory").
 */
function useInsightHistory({ runEndpoint, historyEndpoint, baseDeleteEndpoint }) {
  const [history, setHistory] = useState([]);
  const [active, setActive] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [generating, setGenerating] = useState(false);

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const { data } = await api.get(historyEndpoint);
      setHistory(data.items || []);
      if ((data.items || []).length > 0) setActive(data.items[0]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo cargar el historial");
    } finally { setLoadingHistory(false); }
  };
  useEffect(() => { loadHistory(); /* eslint-disable-next-line */ }, []);

  const runNew = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(runEndpoint);
      setActive(data);
      setHistory((prev) => [data, ...prev]);
      toast.success("Análisis generado por Alendra AI ✨");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo generar el análisis");
    } finally { setGenerating(false); }
  };

  const remove = async (aid) => {
    if (!confirm("¿Eliminar este análisis del historial?")) return;
    try {
      await api.delete(`${baseDeleteEndpoint}/${aid}`);
      setHistory((prev) => prev.filter((h) => h.id !== aid));
      if (active?.id === aid) {
        const remaining = history.filter((h) => h.id !== aid);
        setActive(remaining.length ? remaining[0] : null);
      }
      toast.success("Análisis eliminado");
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  return { history, active, setActive, loadingHistory, generating, runNew, remove, setHistory };
}

function HistorySidebar({ history, active, setActive, loadingHistory, remove, renderItemSubtitle, testidPrefix }) {
  return (
    <aside className="border border-zinc-200 rounded-lg bg-white p-2 max-h-[calc(100vh-220px)] overflow-y-auto" data-testid={`${testidPrefix}-history`}>
      <div className="px-2 py-1.5 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold flex items-center gap-1.5">
        <ClockClockwise size={12}/> Historial
        <span className="ml-auto bg-zinc-100 px-1.5 rounded text-[9px]">{history.length}</span>
      </div>
      {loadingHistory && <div className="text-xs text-zinc-400 p-3 text-center"><Spinner size={14} className="animate-spin inline mr-1"/>Cargando…</div>}
      {!loadingHistory && history.length === 0 && (
        <div className="text-xs text-zinc-400 p-3 text-center">
          <Robot size={28} className="inline mb-1 text-zinc-300"/>
          <div>Sin análisis previos</div>
        </div>
      )}
      <ul className="space-y-1">
        {history.map((h) => {
          const isActive = active?.id === h.id;
          return (
            <li key={h.id} className="relative group">
              <button
                type="button"
                onClick={() => setActive(h)}
                data-testid={`${testidPrefix}-history-item-${h.id}`}
                className={`w-full text-left px-2.5 py-2 pr-8 rounded text-xs transition ${isActive ? "bg-[#002FA7] text-white" : "hover:bg-zinc-100 text-zinc-800"}`}
              >
                <div className="font-semibold truncate">
                  {new Date(h.generated_at).toLocaleDateString("es-MX", { day:"2-digit", month:"short", year:"2-digit" })}
                  <span className={`ml-2 font-normal ${isActive ? "text-white/70" : "text-zinc-400"}`}>
                    {new Date(h.generated_at).toLocaleTimeString("es-MX", { hour:"2-digit", minute:"2-digit" })}
                  </span>
                </div>
                {renderItemSubtitle && renderItemSubtitle(h, isActive)}
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); remove(h.id); }}
                data-testid={`${testidPrefix}-delete-${h.id}`}
                className={`absolute right-1 top-1.5 opacity-0 group-hover:opacity-100 transition p-1 rounded ${isActive ? "hover:bg-white/20 text-white" : "hover:bg-red-100 text-red-600"}`}
                title="Eliminar"
              >
                <Trash size={11}/>
              </button>
            </li>
          );
        })}
      </ul>
    </aside>
  );
}

function Section({ title, icon, children, testId }) {
  return (
    <div className="border border-zinc-200 rounded-lg p-4 bg-white" data-testid={testId}>
      <h3 className="font-semibold text-sm mb-3 inline-flex items-center gap-2 text-zinc-800">{icon}{title}</h3>
      {children}
    </div>
  );
}

function KPI({ icon, label, value, highlight }) {
  return (
    <div className={`p-3 rounded-lg border ${highlight ? "border-amber-300 bg-amber-50" : "border-zinc-200 bg-white"}`}>
      <div className="flex items-center gap-1.5 text-xs text-zinc-500 mb-1">{icon}{label}</div>
      <div className="text-2xl font-bold tracking-tight">{value}</div>
    </div>
  );
}

const fmtMXN = (n) => `$${Number(n || 0).toLocaleString("es-MX", { maximumFractionDigits: 0 })}`;

// ─────────────────────── TAB 1: Inventario ───────────────────────────────────
function InventoryInsightsTab() {
  const ctx = useInsightHistory({
    runEndpoint: "/alendra/insights/inventory",
    historyEndpoint: "/alendra/insights/history",
    baseDeleteEndpoint: "/alendra/insights",
  });
  const [assignFor, setAssignFor] = useState(null);  // {item, index, type}
  const [chatOpen, setChatOpen] = useState(false);

  const toggleAction = async (idx, currentlyCompleted) => {
    if (!ctx.active) return;
    const newCompleted = !currentlyCompleted;
    ctx.setActive((prev) => {
      const set = new Set(prev.completed_actions || []);
      if (newCompleted) set.add(idx); else set.delete(idx);
      return { ...prev, completed_actions: Array.from(set).sort((a,b)=>a-b) };
    });
    try {
      await api.patch(`/alendra/insights/${ctx.active.id}/action`, { action_index: idx, completed: newCompleted });
    } catch {
      toast.error("No se pudo actualizar la acción");
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
      <div>
        <Button onClick={ctx.runNew} disabled={ctx.generating} className="w-full bg-[#002FA7] hover:bg-[#002277] text-white gap-2 mb-3" data-testid="insights-run">
          {ctx.generating ? <Spinner className="animate-spin" size={16}/> : <ArrowsClockwise size={16}/>}
          {ctx.history.length > 0 ? "Generar nuevo" : "Analizar inventario"}
        </Button>
        <HistorySidebar
          testidPrefix="insights"
          {...ctx}
          renderItemSubtitle={(h, isActive) => {
            const done = (h.completed_actions || []).length;
            const tot = (h.insights?.actions || []).length;
            return (
              <div className="flex items-center gap-2 mt-1">
                <div className={`text-[10px] ${isActive ? "text-white/80" : "text-zinc-500"}`}>{done}/{tot} acciones ✓</div>
                {tot > 0 && (
                  <div className={`flex-1 h-1 rounded-full overflow-hidden ${isActive ? "bg-white/20" : "bg-zinc-200"}`}>
                    <div className={`h-full ${done === tot && tot > 0 ? "bg-emerald-400" : isActive ? "bg-amber-300" : "bg-[#002FA7]"}`} style={{ width: `${tot ? (done/tot)*100 : 0}%` }}/>
                  </div>
                )}
              </div>
            );
          }}
        />
      </div>
      <section>
        {!ctx.active && !ctx.generating && (
          <EmptyState icon={<Robot size={48} className="text-zinc-300"/>} text="Da click en Analizar inventario para que Alendra revise stock, ventas y publicaciones."/>
        )}
        {ctx.generating && <LoadingState text="Procesando inventario y consultando a Alendra…"/>}
        {ctx.active && (
          <>
            <div className="flex justify-end mb-2">
              <ChatButton onClick={() => setChatOpen(true)}/>
            </div>
            <InventoryDetail data={ctx.active} toggleAction={toggleAction} onAssign={(item, index) => setAssignFor({ item, index, type: item.action })}/>
          </>
        )}
      </section>
      {assignFor && ctx.active && (
        <TaskAssignModal
          source="inventory"
          analysisId={ctx.active.id}
          itemIndex={assignFor.index}
          item={assignFor.item}
          defaultType={assignFor.type}
          defaultRole={["video","reel","tiktok","historia","campana_pagada"].includes(assignFor.type) ? "marketing" : ["promo","descuento","combo","mover_marketplace"].includes(assignFor.type) ? "sales" : "marketing"}
          onClose={() => setAssignFor(null)}
        />
      )}
      {chatOpen && ctx.active && (
        <AnalysisChatPanel kind="inventory" analysisId={ctx.active.id} onClose={() => setChatOpen(false)}/>
      )}
    </div>
  );
}

function ChatButton({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="open-chat-btn"
      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded border border-zinc-200 bg-white hover:border-[#002FA7] hover:text-[#002FA7] transition"
    >
      <ChatCircle size={14}/> Chat sobre este análisis
    </button>
  );
}

function AssignButton({ onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded bg-[#002FA7]/10 text-[#002FA7] hover:bg-[#002FA7] hover:text-white transition"
      title="Asignar tarea al equipo"
    >
      <UsersThree size={11} weight="bold"/> Asignar
    </button>
  );
}

function InventoryDetail({ data, toggleAction, onAssign }) {
  const completed = new Set(data.completed_actions || []);
  const actions = data.insights?.actions || [];
  const doneCount = actions.reduce((acc, _, i) => acc + (completed.has(i) ? 1 : 0), 0);
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI icon={<Package size={18}/>} label="Productos activos" value={data.snapshot.total_products}/>
        <KPI icon={<Lightning size={18}/>} label="Valor de stock" value={fmtMXN(data.snapshot.total_stock_value_mxn)}/>
        <KPI icon={<Warning size={18}/>} label="Slow movers" value={(data.snapshot.slow_movers || []).length} highlight={(data.snapshot.slow_movers || []).length > 0}/>
        <KPI icon={<Sparkle size={18}/>} label="Fast movers" value={(data.snapshot.fast_movers || []).length}/>
      </div>
      {data.insights?.summary && (
        <Section title="Resumen ejecutivo" icon={<Robot size={18} weight="fill"/>}>
          {Array.isArray(data.insights.summary)
            ? <ul className="space-y-1 text-sm">{data.insights.summary.map((s,i)=><li key={i}>• {s}</li>)}</ul>
            : <p className="text-sm">{data.insights.summary}</p>}
        </Section>
      )}
      {(data.insights?.alerts || []).length > 0 && (
        <Section title="Alertas críticas" icon={<Warning size={18} weight="fill" className="text-amber-600"/>}>
          <ul className="space-y-1.5 text-sm">{data.insights.alerts.map((a,i)=>(<li key={i} className="p-2 rounded bg-amber-50 border border-amber-200 text-amber-900">⚠️ {a}</li>))}</ul>
        </Section>
      )}
      {actions.length > 0 && (
        <Section title={`Acciones recomendadas — ${doneCount}/${actions.length} completadas`} icon={<Lightning size={18} weight="fill" className="text-[#002FA7]"/>}>
          <div className="space-y-1.5" data-testid="insights-actions-list">
            {actions.map((a,i)=>{
              const done = completed.has(i);
              return (
                <div key={i} data-testid={`insights-action-${i}`} className={`p-2.5 rounded border flex items-start gap-2 text-sm transition ${done ? "border-emerald-200 bg-emerald-50/50" : "border-zinc-200 bg-white hover:border-zinc-300"}`}>
                  <button type="button" onClick={() => toggleAction(i, done)} data-testid={`insights-action-toggle-${i}`} className={`shrink-0 mt-0.5 transition ${done ? "text-emerald-600" : "text-zinc-300 hover:text-[#002FA7]"}`}>
                    {done ? <CheckCircle size={20} weight="fill"/> : <Circle size={20}/>}
                  </button>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase shrink-0 ${
                    a.action === "promo" ? "bg-rose-100 text-rose-700" :
                    a.action === "descuento" ? "bg-orange-100 text-orange-700" :
                    a.action === "combo" ? "bg-violet-100 text-violet-700" :
                    a.action === "reel" || a.action === "video" ? "bg-indigo-100 text-indigo-700" :
                    a.action === "mover_marketplace" ? "bg-emerald-100 text-emerald-700" :
                    "bg-zinc-100 text-zinc-700"
                  }`}>{a.action}</span>
                  <div className="flex-1 min-w-0">
                    <div className={`font-semibold ${done ? "line-through text-zinc-500" : "text-zinc-800"}`}>{a.product_name}</div>
                    <div className={`text-xs mt-0.5 ${done ? "text-zinc-400" : "text-zinc-600"}`}>{a.reason}</div>
                  </div>
                  {!done && onAssign && (
                    <AssignButton onClick={() => onAssign(a, i)} testId={`insights-action-assign-${i}`}/>
                  )}
                </div>
              );
            })}
          </div>
        </Section>
      )}
      {(data.insights?.top_opportunities || []).length > 0 && (
        <Section title="Oportunidades de venta" icon={<Lightbulb size={18} weight="fill" className="text-emerald-600"/>}>
          <ul className="space-y-1.5 text-sm">{data.insights.top_opportunities.map((o,i)=>(<li key={i} className="p-2 rounded bg-emerald-50 border border-emerald-200 text-emerald-900">💡 {o}</li>))}</ul>
        </Section>
      )}
      <p className="text-[10px] text-zinc-400 text-right">Generado: {new Date(data.generated_at).toLocaleString("es-MX")}</p>
    </div>
  );
}

// ─────────────────────── TAB 2: Marketing ────────────────────────────────────
const CONTENT_ICONS = {
  video: VideoCamera, reel: FilmReel, tiktok: MusicNote, historia: BookOpenText, campana_pagada: Megaphone,
};
const CONTENT_COLORS = {
  video: "bg-blue-100 text-blue-700",
  reel: "bg-pink-100 text-pink-700",
  tiktok: "bg-zinc-900 text-white",
  historia: "bg-purple-100 text-purple-700",
  campana_pagada: "bg-emerald-100 text-emerald-700",
};

function MarketingInsightsTab() {
  const ctx = useInsightHistory({
    runEndpoint: "/alendra/insights/marketing",
    historyEndpoint: "/alendra/insights/marketing/history",
    baseDeleteEndpoint: "/alendra/insights/marketing",
  });
  const [assignFor, setAssignFor] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);

  const toggleRec = async (idx, currentlyCompleted) => {
    if (!ctx.active) return;
    const newCompleted = !currentlyCompleted;
    ctx.setActive((prev) => {
      const set = new Set(prev.completed_recommendations || []);
      if (newCompleted) set.add(idx); else set.delete(idx);
      return { ...prev, completed_recommendations: Array.from(set).sort((a,b)=>a-b) };
    });
    try {
      await api.patch(`/alendra/insights/marketing/${ctx.active.id}/recommendation`, { rec_index: idx, completed: newCompleted });
    } catch {
      toast.error("No se pudo actualizar");
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
      <div>
        <Button onClick={ctx.runNew} disabled={ctx.generating} className="w-full bg-pink-600 hover:bg-pink-700 text-white gap-2 mb-3" data-testid="marketing-run">
          {ctx.generating ? <Spinner className="animate-spin" size={16}/> : <ArrowsClockwise size={16}/>}
          {ctx.history.length > 0 ? "Nuevo análisis" : "Analizar marketing"}
        </Button>
        <HistorySidebar
          testidPrefix="marketing"
          {...ctx}
          renderItemSubtitle={(h, isActive) => {
            const done = (h.completed_recommendations || []).length;
            const tot = (h.result?.recommendations || []).length;
            return (
              <div className={`text-[10px] mt-1 ${isActive ? "text-white/80" : "text-zinc-500"}`}>{done}/{tot} contenidos ✓</div>
            );
          }}
        />
      </div>
      <section>
        {!ctx.active && !ctx.generating && (
          <EmptyState icon={<Megaphone size={48} className="text-zinc-300"/>} text="Da click en Analizar marketing para que Alendra te diga qué productos merecen video, reel, TikTok, historia o campaña pagada."/>
        )}
        {ctx.generating && <LoadingState text="Identificando productos con mejor ROI de contenido…"/>}
        {ctx.active && (
          <>
            <div className="flex justify-end mb-2"><ChatButton onClick={() => setChatOpen(true)}/></div>
            <MarketingDetail data={ctx.active} toggleRec={toggleRec} onAssign={(item, index) => setAssignFor({ item, index, type: item.content_type })}/>
          </>
        )}
      </section>
      {assignFor && ctx.active && (
        <TaskAssignModal
          source="marketing"
          analysisId={ctx.active.id}
          itemIndex={assignFor.index}
          item={assignFor.item}
          defaultType={assignFor.type}
          defaultRole="marketing"
          onClose={() => setAssignFor(null)}
        />
      )}
      {chatOpen && ctx.active && (
        <AnalysisChatPanel kind="marketing" analysisId={ctx.active.id} onClose={() => setChatOpen(false)}/>
      )}
    </div>
  );
}

function MarketingDetail({ data, toggleRec, onAssign }) {
  const completed = new Set(data.completed_recommendations || []);
  const recs = data.result?.recommendations || [];
  const doneCount = recs.reduce((acc, _, i) => acc + (completed.has(i) ? 1 : 0), 0);
  return (
    <div className="space-y-4">
      {data.result?.summary && (
        <Section title="Estrategia recomendada" icon={<Robot size={18} weight="fill"/>}>
          <p className="text-sm">{data.result.summary}</p>
        </Section>
      )}
      {recs.length > 0 && (
        <Section title={`Productos para producir contenido — ${doneCount}/${recs.length} hechos`} icon={<Megaphone size={18} weight="fill" className="text-pink-600"/>}>
          <div className="space-y-2" data-testid="marketing-recs-list">
            {recs.map((r,i)=>{
              const done = completed.has(i);
              const Icon = CONTENT_ICONS[r.content_type] || Sparkle;
              const color = CONTENT_COLORS[r.content_type] || "bg-zinc-100 text-zinc-700";
              return (
                <div key={i} data-testid={`marketing-rec-${i}`} className={`p-3 rounded border flex items-start gap-3 ${done ? "border-emerald-200 bg-emerald-50/40" : "border-zinc-200 bg-white"}`}>
                  <button type="button" onClick={() => toggleRec(i, done)} data-testid={`marketing-rec-toggle-${i}`} className={`shrink-0 mt-0.5 ${done ? "text-emerald-600" : "text-zinc-300 hover:text-pink-600"}`}>
                    {done ? <CheckCircle size={20} weight="fill"/> : <Circle size={20}/>}
                  </button>
                  <div className={`shrink-0 w-9 h-9 rounded-lg ${color} flex items-center justify-center`}>
                    <Icon size={18} weight="fill"/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`font-semibold text-sm ${done ? "line-through text-zinc-500" : "text-zinc-800"}`}>{r.product_name}</span>
                      <span className={`text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded ${color}`}>{r.content_type?.replace("_", " ")}</span>
                      {r.priority && <span className={`text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded ${r.priority === "alta" ? "bg-rose-100 text-rose-700" : r.priority === "media" ? "bg-amber-100 text-amber-700" : "bg-zinc-100 text-zinc-600"}`}>{r.priority}</span>}
                      {r.estimated_roi && <span className="text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">ROI {r.estimated_roi}</span>}
                    </div>
                    {r.hook && <div className="text-sm italic text-zinc-700 mt-1">🎬 &ldquo;{r.hook}&rdquo;</div>}
                    {r.reason && <div className="text-xs text-zinc-500 mt-1">{r.reason}</div>}
                  </div>
                  {!done && onAssign && (
                    <AssignButton onClick={() => onAssign(r, i)} testId={`marketing-rec-assign-${i}`}/>
                  )}
                </div>
              );
            })}
          </div>
        </Section>
      )}
      {(data.result?.skip || []).length > 0 && (
        <Section title="Productos que NO vale la pena trabajar" icon={<Warning size={18} weight="fill" className="text-zinc-500"/>}>
          <ul className="space-y-1 text-xs text-zinc-600">
            {data.result.skip.map((s,i)=>(<li key={i}>• {s}</li>))}
          </ul>
        </Section>
      )}
      <p className="text-[10px] text-zinc-400 text-right">Generado: {new Date(data.generated_at).toLocaleString("es-MX")}</p>
    </div>
  );
}

// ─────────────────────── TAB 3: Compras ──────────────────────────────────────
function PurchasingInsightsTab() {
  const ctx = useInsightHistory({
    runEndpoint: "/alendra/insights/purchasing",
    historyEndpoint: "/alendra/insights/purchasing/history",
    baseDeleteEndpoint: "/alendra/insights/purchasing",
  });
  const [assignFor, setAssignFor] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);

  const toggleBuy = async (idx, currentlyCompleted) => {
    if (!ctx.active) return;
    const newCompleted = !currentlyCompleted;
    ctx.setActive((prev) => {
      const set = new Set(prev.completed_purchases || []);
      if (newCompleted) set.add(idx); else set.delete(idx);
      return { ...prev, completed_purchases: Array.from(set).sort((a,b)=>a-b) };
    });
    try {
      await api.patch(`/alendra/insights/purchasing/${ctx.active.id}/purchase`, { buy_index: idx, completed: newCompleted });
    } catch {
      toast.error("No se pudo actualizar");
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
      <div>
        <Button onClick={ctx.runNew} disabled={ctx.generating} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white gap-2 mb-3" data-testid="purchasing-run">
          {ctx.generating ? <Spinner className="animate-spin" size={16}/> : <ArrowsClockwise size={16}/>}
          {ctx.history.length > 0 ? "Nuevo análisis" : "Analizar compras"}
        </Button>
        <HistorySidebar
          testidPrefix="purchasing"
          {...ctx}
          renderItemSubtitle={(h, isActive) => {
            const done = (h.completed_purchases || []).length;
            const tot = (h.result?.buy_more || []).length;
            return (
              <div className={`text-[10px] mt-1 ${isActive ? "text-white/80" : "text-zinc-500"}`}>{done}/{tot} compras ✓</div>
            );
          }}
        />
      </div>
      <section>
        {!ctx.active && !ctx.generating && (
          <EmptyState icon={<ShoppingCart size={48} className="text-zinc-300"/>} text="Da click en Analizar compras para que Alendra revise tu rotación y márgenes, y te diga qué reabastecer y qué dejar de comprar."/>
        )}
        {ctx.generating && <LoadingState text="Analizando ventas, márgenes y stock…"/>}
        {ctx.active && (
          <>
            <div className="flex justify-end mb-2"><ChatButton onClick={() => setChatOpen(true)}/></div>
            <PurchasingDetail data={ctx.active} toggleBuy={toggleBuy} onAssign={(item, index, type) => setAssignFor({ item, index, type })}/>
          </>
        )}
      </section>
      {assignFor && ctx.active && (
        <TaskAssignModal
          source="purchasing"
          analysisId={ctx.active.id}
          itemIndex={assignFor.index}
          item={{...assignFor.item, reason: assignFor.item.reason}}
          defaultType={assignFor.type}
          defaultRole="inventory"
          onClose={() => setAssignFor(null)}
        />
      )}
      {chatOpen && ctx.active && (
        <AnalysisChatPanel kind="purchasing" analysisId={ctx.active.id} onClose={() => setChatOpen(false)}/>
      )}
    </div>
  );
}

function PurchasingDetail({ data, toggleBuy, onAssign }) {
  const completed = new Set(data.completed_purchases || []);
  const buyMore = data.result?.buy_more || [];
  const stopBuying = data.result?.stop_buying || [];
  const bestMargin = data.result?.best_margin || [];
  const bestRotation = data.result?.best_rotation || [];
  const totalRevenue = buyMore.reduce((s, b) => s + Number(b.expected_revenue_mxn || 0), 0);
  const totalStuck = stopBuying.reduce((s, b) => s + Number(b.stuck_value_mxn || 0), 0);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI icon={<TrendUp size={18}/>} label="Reabastecer" value={buyMore.length}/>
        <KPI icon={<TrendDown size={18}/>} label="Dejar de comprar" value={stopBuying.length} highlight={stopBuying.length > 0}/>
        <KPI icon={<CurrencyDollar size={18}/>} label="Revenue potencial" value={fmtMXN(totalRevenue)}/>
        <KPI icon={<Warning size={18}/>} label="Valor parado" value={fmtMXN(totalStuck)} highlight={totalStuck > 0}/>
      </div>
      {data.result?.summary && (
        <Section title="Resumen de compras" icon={<Robot size={18} weight="fill"/>}>
          <p className="text-sm">{data.result.summary}</p>
        </Section>
      )}
      {buyMore.length > 0 && (
        <Section title="✅ Comprar más" icon={<TrendUp size={18} weight="fill" className="text-emerald-600"/>} testId="purchasing-buy-more">
          <div className="space-y-1.5">
            {buyMore.map((b,i)=>{
              const done = completed.has(i);
              return (
                <div key={i} data-testid={`purchasing-buy-${i}`} className={`p-2.5 rounded border flex items-start gap-2 text-sm ${done ? "border-emerald-200 bg-emerald-50/40" : "border-zinc-200 bg-white"}`}>
                  <button type="button" onClick={() => toggleBuy(i, done)} data-testid={`purchasing-buy-toggle-${i}`} className={`shrink-0 mt-0.5 ${done ? "text-emerald-600" : "text-zinc-300 hover:text-emerald-600"}`}>
                    {done ? <CheckCircle size={20} weight="fill"/> : <Circle size={20}/>}
                  </button>
                  <span className="shrink-0 px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-emerald-100 text-emerald-700">x{b.suggested_qty}</span>
                  <div className="flex-1 min-w-0">
                    <div className={`font-semibold ${done ? "line-through text-zinc-500" : "text-zinc-800"}`}>{b.product_name}</div>
                    <div className="text-xs text-zinc-600 mt-0.5">{b.reason}</div>
                  </div>
                  {b.expected_revenue_mxn ? (
                    <div className="text-right shrink-0">
                      <div className="text-[10px] text-zinc-400 uppercase tracking-wider">Rev. esp.</div>
                      <div className="font-bold text-emerald-700 text-sm">{fmtMXN(b.expected_revenue_mxn)}</div>
                    </div>
                  ) : null}
                  {!done && onAssign && (
                    <AssignButton onClick={() => onAssign(b, i, "comprar_mas")} testId={`purchasing-buy-assign-${i}`}/>
                  )}
                </div>
              );
            })}
          </div>
        </Section>
      )}
      {stopBuying.length > 0 && (
        <Section title="🛑 Dejar de comprar" icon={<TrendDown size={18} weight="fill" className="text-rose-600"/>} testId="purchasing-stop-buying">
          <div className="space-y-1.5">
            {stopBuying.map((s,i)=>(
              <div key={i} className="p-2.5 rounded border border-rose-200 bg-rose-50/40 flex items-start gap-2 text-sm">
                <Warning size={16} weight="fill" className="text-rose-500 shrink-0 mt-0.5"/>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-zinc-800">{s.product_name}</div>
                  <div className="text-xs text-zinc-600 mt-0.5">{s.reason}</div>
                </div>
                {s.stuck_value_mxn ? (
                  <div className="text-right shrink-0">
                    <div className="text-[10px] text-zinc-400 uppercase tracking-wider">Valor parado</div>
                    <div className="font-bold text-rose-700 text-sm">{fmtMXN(s.stuck_value_mxn)}</div>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </Section>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {bestMargin.length > 0 && (
          <Section title="💎 Mejor margen" icon={<ChartBar size={18} weight="fill" className="text-violet-600"/>}>
            <ul className="space-y-1 text-sm">
              {bestMargin.map((m,i)=>(<li key={i} className="flex items-center gap-2"><span className="font-semibold flex-1 truncate">{m.product_name}</span><span className="font-mono text-violet-700">{Number(m.margin_pct).toFixed(1)}%</span></li>))}
            </ul>
          </Section>
        )}
        {bestRotation.length > 0 && (
          <Section title="⚡ Mejor rotación" icon={<Lightning size={18} weight="fill" className="text-amber-600"/>}>
            <ul className="space-y-1 text-sm">
              {bestRotation.map((m,i)=>(<li key={i} className="flex items-center gap-2"><span className="font-semibold flex-1 truncate">{m.product_name}</span><span className="font-mono text-amber-700">{Number(m.velocity_per_month).toFixed(2)}/mes</span></li>))}
            </ul>
          </Section>
        )}
      </div>
      <p className="text-[10px] text-zinc-400 text-right">Generado: {new Date(data.generated_at).toLocaleString("es-MX")}</p>
    </div>
  );
}

// ─────────────────────── Shared ──────────────────────────────────────────────
function EmptyState({ icon, text }) {
  return (
    <div className="border border-zinc-200 rounded-lg p-10 text-center bg-zinc-50 h-full flex flex-col items-center justify-center">
      <div className="mb-2">{icon}</div>
      <p className="text-sm text-zinc-500 max-w-md">{text}</p>
    </div>
  );
}

function LoadingState({ text }) {
  return (
    <div className="border border-zinc-200 rounded-lg p-10 text-center bg-white">
      <Spinner size={32} className="animate-spin inline text-[#002FA7]"/>
      <p className="text-sm text-zinc-500 mt-2">{text}</p>
    </div>
  );
}
